/**
 * RunASaleModal — Full-screen promotion creation dialog.
 *
 * Two-column layout matching FieldSheet design system:
 * - Left: Promotion type picker (2×2 grid with icon cards)
 * - Right: Settings form + live preview
 * - Footer: Cancel + Launch Sale
 *
 * Part of Plan 34 (Pricing Engine), redesigned from 3-step wizard.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { X, Loader2, Percent, Banknote, Package, Users, Users2 } from "lucide-react";
import { toast } from "sonner";
import { PromotionFormFields } from "./PromotionFormFields";
import { LivePreviewPanel } from "./LivePreviewPanel";
import {
  createEmptyFormState,
  createFormStateFromPromotion,
  getTypeConfig,
  computePreviewPrice,
  PROMOTION_TYPES,
  type PromotionTypeId,
  type PromotionFormState,
} from "./promotionTypes";
import { useCreatePromotion } from "@/features/inventory/api/useCreatePromotion";
import { useDeletePromotion } from "@/features/inventory/api/useDeletePromotion";
import { useUpdatePromotion } from "@/features/inventory/api/useUpdatePromotion";
import { useCheckConflicts } from "@/features/inventory/api/useCheckConflicts";
import { usePricingCustomerGroups } from "@/features/inventory/api/pricingSummaryHooks";
import { useLanguage } from "@/i18n/useLanguage";
import type { Promotion } from "@/features/inventory/api/pricingSummaryHooks";
import type { LucideIcon } from "lucide-react";

// ── i18n ────────────────────────────────────────────────────

const i18n = {
  runASale: { en: "Run a Sale", th: "จัดโปรโมชัน" },
  editPromotion: { en: "Edit Promotion", th: "แก้ไขโปรโมชัน" },
  runASaleDesc: { en: "Choose a promotion type and configure settings.", th: "เลือกประเภทโปรโมชันและตั้งค่า" },
  editDesc: { en: "Update this promotion's settings.", th: "อัปเดตการตั้งค่าโปรโมชันนี้" },
  promotionType: { en: "Promotion Type", th: "ประเภทโปรโมชัน" },
  settings: { en: "Settings", th: "การตั้งค่า" },
  selectType: { en: "Select a promotion type to configure", th: "เลือกประเภทโปรโมชันเพื่อตั้งค่า" },
  cancel: { en: "Cancel", th: "ยกเลิก" },
  launchSale: { en: "Launch Sale", th: "เปิดโปรโมชัน" },
  saveChanges: { en: "Save Changes", th: "บันทึกการเปลี่ยนแปลง" },
  launching: { en: "Launching…", th: "กำลังเปิด…" },
  saving: { en: "Saving…", th: "กำลังบันทึก…" },
  // Type labels
  percentageOff: { en: "Percentage Off", th: "ลดเป็นเปอร์เซ็นต์" },
  percentageOffDesc: { en: "e.g. 15% off for everyone", th: "เช่น ลด 15% สำหรับทุกคน" },
  fixedSalePrice: { en: "Fixed Sale Price", th: "ราคาลดคงที่" },
  fixedSalePriceDesc: { en: "e.g. ฿199 during the weekend", th: "เช่น ฿199 ช่วงสุดสัปดาห์" },
  quantityDeal: { en: "Quantity Deal", th: "ส่วนลดตามจำนวน" },
  quantityDealDesc: { en: "e.g. Buy 5+, pay 10% less", th: "เช่น ซื้อ 5+ ลด 10%" },
  customerGroupPrice: { en: "Customer Group Price", th: "ราคากลุ่มลูกค้า" },
  customerGroupPriceDesc: { en: "e.g. VIP members always pay ฿220", th: "เช่น สมาชิก VIP จ่าย ฿220" },
  groupOrderDeal: { en: "Group / Bulk Deal", th: "ดีลกลุ่ม / เหมาจำนวน" },
  groupOrderDealDesc: { en: "e.g. order 10+ → free shipping + free logo", th: "เช่น สั่ง 10+ → ส่งฟรี + ปักโลโก้ฟรี" },
  // Toasts
  saleIsLive: { en: "Sale is live", th: "โปรโมชันเปิดแล้ว" },
  undo: { en: "Undo", th: "ย้อนกลับ" },
  saleRemoved: { en: "Sale removed", th: "ลบโปรโมชันแล้ว" },
  undoFailed: { en: "Failed to undo. Please remove the sale manually.", th: "ย้อนกลับไม่ได้ กรุณาลบโปรโมชันเอง" },
  promotionUpdated: { en: "Promotion updated", th: "อัปเดตโปรโมชันแล้ว" },
} as const;

// ── Type card config ────────────────────────────────────────

const TYPE_ICONS: Record<PromotionTypeId, LucideIcon> = {
  percentage_off: Percent,
  fixed_sale_price: Banknote,
  quantity_deal: Package,
  customer_group_price: Users,
  group_order_deal: Users2,
};

const TYPE_I18N: Record<PromotionTypeId, { label: keyof typeof i18n; desc: keyof typeof i18n }> = {
  percentage_off: { label: "percentageOff", desc: "percentageOffDesc" },
  fixed_sale_price: { label: "fixedSalePrice", desc: "fixedSalePriceDesc" },
  quantity_deal: { label: "quantityDeal", desc: "quantityDealDesc" },
  customer_group_price: { label: "customerGroupPrice", desc: "customerGroupPriceDesc" },
  group_order_deal: { label: "groupOrderDeal", desc: "groupOrderDealDesc" },
};

// ── Props ────────────────────────────────────────────────────

interface RunASaleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemCode: string;
  itemName: string;
  basePrice: number | null;
  currency: string;
  /** When provided, modal opens in edit mode (pre-filled, type locked) */
  editPromotion?: Promotion;
}

// ── Component ────────────────────────────────────────────────

export function RunASaleModal({
  open,
  onOpenChange,
  itemCode,
  itemName: _itemName,
  basePrice,
  currency,
  editPromotion,
}: RunASaleModalProps) {
  const { language } = useLanguage();
  const t = (key: keyof typeof i18n) => i18n[key][language] || i18n[key].en;

  const [form, setForm] = useState<PromotionFormState>(createEmptyFormState);
  const [conflictResolution, setConflictResolution] = useState<"replace" | "change_dates" | null>(null);
  const [checkConflictsEnabled, setCheckConflictsEnabled] = useState(false);

  const isEditMode = !!editPromotion;
  const newRuleNameRef = useRef<string | null>(null);
  const createMutation = useCreatePromotion(itemCode);
  const updateMutation = useUpdatePromotion(itemCode);
  const deleteMutation = useDeletePromotion(itemCode);
  const groupsQuery = usePricingCustomerGroups();

  const conflictsQuery = useCheckConflicts(
    itemCode,
    form.validFrom,
    form.validUpto || null,
    checkConflictsEnabled
  );

  const conflicts = conflictsQuery.data ?? [];

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      if (editPromotion) {
        setForm(createFormStateFromPromotion(editPromotion));
      } else {
        setForm(createEmptyFormState());
      }
      setConflictResolution(null);
      setCheckConflictsEnabled(false);
      createMutation.reset();
      updateMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFormChange = useCallback(
    (field: keyof PromotionFormState, value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      if (field === "validUpto" || field === "validFrom") {
        setCheckConflictsEnabled(false);
        setConflictResolution(null);
      }
    },
    []
  );

  const handleToggleStacking = useCallback(() => {
    setForm((prev) => ({ ...prev, applyMultiplePricingRules: !prev.applyMultiplePricingRules }));
  }, []);

  const handleTypeSelect = useCallback((type: PromotionTypeId) => {
    setForm((prev) => ({
      ...prev,
      promotionType: type,
      customerGroup: type === "customer_group_price" ? "" : "All Customers",
    }));
  }, []);

  const handleCheckConflicts = useCallback(() => {
    setCheckConflictsEnabled(true);
  }, []);

  const handleConflictResolution = useCallback((action: "replace" | "change_dates") => {
    setConflictResolution(action);
  }, []);

  // Validation
  const canLaunch = (() => {
    if (!form.promotionType) return false;
    const config = getTypeConfig(form.promotionType);
    if (!form.saleName.trim()) return false;
    if (config.valueField === "discount_percentage") {
      const pct = parseFloat(form.discountPercentage);
      if (isNaN(pct) || pct <= 0 || pct > 100) return false;
    } else {
      const r = parseFloat(form.rate);
      if (isNaN(r) || r <= 0) return false;
    }
    if (config.hasMinQty && (!form.minQty || parseInt(form.minQty) <= 0)) return false;
    if (config.hasEndDate && !form.validUpto) return false;
    if (!form.validFrom) return false;
    if (form.promotionType === "customer_group_price" && !form.customerGroup) return false;
    if (conflicts.length > 0 && conflictResolution === null) return false;
    return true;
  })();

  const handleLaunchSale = useCallback(() => {
    if (!form.promotionType) return;
    const config = getTypeConfig(form.promotionType);

    const params = {
      item_code: itemCode,
      promotion_type: form.promotionType,
      sale_name: form.saleName,
      ...(config.valueField === "discount_percentage"
        ? { discount_percentage: parseFloat(form.discountPercentage) }
        : { rate: parseFloat(form.rate) }),
      ...(config.hasMinQty && form.minQty ? { min_qty: parseInt(form.minQty) } : {}),
      ...(form.customerGroup && form.customerGroup !== "All Customers"
        ? { customer_group: form.customerGroup }
        : {}),
      valid_from: form.validFrom,
      ...(config.hasEndDate && form.validUpto ? { valid_upto: form.validUpto } : {}),
      ...(conflictResolution === "replace" && conflicts.length > 0
        ? { replace_rule_name: conflicts[0].name }
        : {}),
      ...(form.applyMultiplePricingRules ? { apply_multiple_pricing_rules: 1 } : { apply_multiple_pricing_rules: 0 }),
    };

    if (isEditMode && editPromotion) {
      updateMutation.mutate(
        { pricing_rule_name: editPromotion.name, ...params },
        {
          onSuccess: () => {
            onOpenChange(false);
            toast.success(t("promotionUpdated"));
          },
        }
      );
    } else {
      createMutation.mutate(params, {
        onSuccess: (response) => {
          newRuleNameRef.current = response.name;
          onOpenChange(false);

          const endLabel = form.validUpto ? ` — ends ${form.validUpto}` : "";
          toast.success(`${t("saleIsLive")}${endLabel}`, {
            duration: 4000,
            action: {
              label: t("undo"),
              onClick: () => {
                const ruleName = newRuleNameRef.current;
                if (!ruleName) return;
                deleteMutation.mutate(ruleName, {
                  onSuccess: () => toast.success(t("saleRemoved")),
                  onError: () => toast.error(t("undoFailed")),
                });
              },
            },
          });
        },
      });
    }
  }, [form, itemCode, isEditMode, editPromotion, conflicts, conflictResolution, createMutation, updateMutation, deleteMutation, onOpenChange, t]);

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const computedPrice = form.promotionType
    ? computePreviewPrice(basePrice, form.promotionType, form)
    : null;
  const hasError = createMutation.isError || updateMutation.isError;
  const errorMessage = hasError
    ? (createMutation.error?.message || updateMutation.error?.message || "Failed to save promotion")
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[92vh] flex flex-col p-0 [&>button.absolute]:hidden">
        {/* Header — custom close button on the right */}
        <div className="flex items-start justify-between px-8 pt-8 pb-4">
          <div>
            <DialogTitle className="text-xl font-semibold">
              {isEditMode ? t("editPromotion") : t("runASale")}
            </DialogTitle>
            <DialogDescription className="mt-1">
              {isEditMode ? t("editDesc") : t("runASaleDesc")}
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="shrink-0 ml-4 mt-0.5 rounded-full p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body — two columns */}
        <div className="flex-1 min-h-0 overflow-y-auto px-8 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left column: Promotion Type */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
                {t("promotionType")}
              </h3>
              <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label={t("promotionType")}>
                {/* Coupon-based orchestration types (e.g. Group / Bulk Deal) are built
                    through the full Pricing Rule wizard, not this item-level modal. */}
                {PROMOTION_TYPES.filter((type) => !type.couponBased).map((type) => {
                  const isSelected = form.promotionType === type.id;
                  const Icon = TYPE_ICONS[type.id];
                  const keys = TYPE_I18N[type.id];
                  return (
                    <button
                      key={type.id}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => handleTypeSelect(type.id)}
                      disabled={isEditMode}
                      className={cn(
                        "flex flex-col items-start gap-2 rounded-md border p-4 text-left transition-all",
                        "hover:bg-accent/50 hover:shadow-sm",
                        isSelected
                          ? "border-primary bg-primary/5 ring-2 ring-primary/20 shadow-sm"
                          : "border-border",
                        isEditMode && !isSelected && "opacity-40 cursor-not-allowed",
                      )}
                    >
                      <div className={cn(
                        "flex items-center justify-center h-10 w-10 rounded",
                        isSelected
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground",
                      )}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{t(keys.label)}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {t(keys.desc)}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right column: Settings + Preview */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
                {t("settings")}
              </h3>
              {form.promotionType ? (
                <div className="space-y-5">
                  <PromotionFormFields
                    typeId={form.promotionType}
                    form={form}
                    onChange={handleFormChange}
                    onToggleStacking={handleToggleStacking}
                    conflicts={conflicts}
                    onCheckConflicts={handleCheckConflicts}
                    customerGroups={groupsQuery.data ?? []}
                    conflictResolution={conflictResolution}
                    onConflictResolution={handleConflictResolution}
                  />

                  {/* Live preview */}
                  <LivePreviewPanel
                    basePrice={basePrice}
                    computedPrice={computedPrice}
                    currency={currency}
                    validFrom={form.validFrom}
                    validUpto={form.validUpto}
                  />

                  {/* Error alert */}
                  {errorMessage && (
                    <Alert variant="destructive">
                      <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  {t("selectType")}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t px-8 py-5">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleLaunchSale}
            disabled={!canLaunch || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditMode ? t("saving") : t("launching")}
              </>
            ) : (
              isEditMode ? t("saveChanges") : t("launchSale")
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
